import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import DstTabsVertical from "../../../../components/DstTabsVertical/DstTabsVertical";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { CohortIntensity } from "../../../../models/CohortIntensity";
import { CursusStageCadre } from "../../../../models/CursusStageCadre";
import { LanguageEnum } from "../../../../models/Language";
import translate from "../../../../services/Translate";
import { CursusStageForm, EditCursusStagePopupProps } from "./EditCursusStagePopup.d";
import { cursusStageToForm, getChangedFields, updateCursusStage } from "./EditCursusStagePopup.function";
import "./EditCursusStagePopup.scss";

const EditCursusStagePopup = ({
  course,
  language,
  onCourseUpdated,
  openPopup,
  setOpenPopup,
}: EditCursusStagePopupProps) => {
  const [activeTab, setActiveTab] = useState("general");
  const [initialForm, setInitialForm] = useState<CursusStageForm>(() => cursusStageToForm(course));
  const [form, setForm] = useState<CursusStageForm>(initialForm);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (openPopup) {
      const nextForm = cursusStageToForm(course);
      setInitialForm(nextForm);
      setForm(nextForm);
      setActiveTab("general");
    }
  }, [course, openPopup]);

  const _updateField = (key: keyof CursusStageForm, value: string | number | boolean | null) => {
    setForm((previousForm) => ({ ...previousForm, [key]: value }));
  };

  const _handleClose = () => {
    if (!isSaving) setOpenPopup(false);
  };

  const areDatesValid = form.dt_start !== "" && form.dt_end !== "" && form.dt_end >= form.dt_start;

  const _handleSubmit = (event?: FormEvent) => {
    event?.preventDefault();

    if (!_isFormValid) return;

    const payload = getChangedFields(form, initialForm);

    if (!Object.keys(payload).length) return;

    updateCursusStage(
      course.id,
      payload,
      setIsSaving,
      () => {
        onCourseUpdated({ ...course, ...payload, dt_updated: new Date().toISOString() });
        setOpenPopup(false);
        alert(translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.ALERTS.SUCCESS"));
      },
      language
    );
  };

  const _isFormValid = useMemo(
    () =>
      form.deployment_name.trim() !== "" && form.cursus_prefix.trim() !== "" && areDatesValid && form.language !== "",
    [form.deployment_name, form.cursus_prefix, areDatesValid, form.language]
  );

  const _hasChanges = useMemo(() => Object.keys(getChangedFields(form, initialForm)).length > 0, [form, initialForm]);

  const textArea = (key: keyof CursusStageForm, rows = 3) => (
    <Col md={6}>
      <Form.Label>{translate(language, `PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.${key.toUpperCase()}`)}</Form.Label>
      <Form.Control
        as="textarea"
        rows={rows}
        value={form[key] as string}
        onChange={(event) => _updateField(key, event.target.value)}
      />
    </Col>
  );

  const _tabTitle = (key: string) => translate(language, `PAGES.COURSE.MODALS.EDIT_STAGE.TABS.${key}`);
  const _sectionTitle = (key: string) => <h3 className="mb-4">{_tabTitle(key)}</h3>;

  const overallInformationTab = (
    <div className="bg-body p-4 h-100 s-regular">
      {_sectionTitle("OVERALL_INFORMATION")}
      <Row className="g-3">
        <Col md={6}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.DEPLOYMENT_NAME")} />
          </Form.Label>
          <Form.Control
            value={form.deployment_name}
            onChange={(event) => _updateField("deployment_name", event.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.CURSUS_PREFIX")} />
          </Form.Label>
          <Form.Control
            value={form.cursus_prefix}
            onChange={(event) => _updateField("cursus_prefix", event.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.DT_START")} />
          </Form.Label>
          <Form.Control
            type="datetime-local"
            value={form.dt_start}
            onChange={(event) => _updateField("dt_start", event.target.value)}
            required
            isInvalid={!areDatesValid}
          />
        </Col>
        <Col md={6}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.DT_END")} />
          </Form.Label>
          <Form.Control
            type="datetime-local"
            value={form.dt_end}
            min={form.dt_start}
            onChange={(event) => _updateField("dt_end", event.target.value)}
            required
            isInvalid={!areDatesValid}
          />
        </Col>
        <Col md={4}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.LANGUAGE")} />
          </Form.Label>
          <Form.Select
            value={form.language}
            onChange={(event) => _updateField("language", event.target.value)}
            required
          >
            {Object.values(LanguageEnum).map((value) => (
              <option key={value} value={value}>
                {value.toUpperCase()}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.DEFAULT_CADRE")} />
          </Form.Label>
          <Form.Select
            value={form.default_cadre}
            onChange={(event) => _updateField("default_cadre", event.target.value)}
            required
          >
            {Object.values(CursusStageCadre).map((value) => (
              <option key={value} value={value}>
                {value.toUpperCase()}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label>{translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.PRIORITY")}</Form.Label>
          <Form.Control
            type="number"
            min="0"
            value={form.priority ?? ""}
            onChange={(event) =>
              _updateField("priority", event.target.value === "" ? null : Number(event.target.value))
            }
          />
        </Col>
        <Col md={8} className="d-flex align-items-end pb-2">
          <Form.Check
            type="switch"
            label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.IS_DISABLED")}
            checked={form.is_disabled}
            onChange={(event) => _updateField("is_disabled", event.target.checked)}
          />
        </Col>
      </Row>
    </div>
  );

  const formatTab = (
    <div className="bg-body p-4 h-100 s-regular">
      {_sectionTitle("FORMAT")}
      <Row className="g-3">
        <Col md={12}>
          <Form.Label>
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.FORMAT")} />
          </Form.Label>
          <Form.Select value={form.format} onChange={(event) => _updateField("format", event.target.value)} required>
            {Object.values(CohortIntensity).map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Form.Select>
        </Col>
        {["total_hours", "hours_spent_on_project", "hours_spent_on_modules", "hours_spent_on_masterclass"].map(
          (key) => (
            <Col md={6} key={key}>
              <Form.Label>
                {translate(language, `PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.${key.toUpperCase()}`)}
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={(form[key as keyof CursusStageForm] as number | null) ?? ""}
                onChange={(event) =>
                  _updateField(
                    key as keyof CursusStageForm,
                    event.target.value === "" ? null : Number(event.target.value)
                  )
                }
              />
            </Col>
          )
        )}
      </Row>
    </div>
  );

  const contractualizationTab = (
    <div className="bg-body p-4 h-100 s-regular">
      {_sectionTitle("CONTRACTUALIZATION")}
      <Row className="g-3">
        {textArea("objectives")}
        {textArea("prerequisites")}
        {textArea("content")}
        {textArea("training_organization")}
        {textArea("evaluations")}
        {textArea("end_of_formation_document")}
        {textArea("mediator")}
        {textArea("pedagogy_leader_references")}
        {textArea("pedagogy_leader_diplomas")}
        <Col md={12}>
          <Form.Label>
            {translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.FIELDS.PRESENTATION_DOCUMENT_URL")}
          </Form.Label>
          <Form.Control
            type="url"
            value={form.presentation_document_url}
            onChange={(event) => _updateField("presentation_document_url", event.target.value)}
          />
        </Col>
      </Row>
    </div>
  );

  return (
    <Popup
      show={openPopup}
      onClose={_handleClose}
      modalTitle={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.TITLE")}
      size="xl"
      scrollable
      removePadding
      footerContent={
        <React.Fragment>
          <DstButton
            variant="light"
            value={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CANCEL")}
            clickFunction={_handleClose}
            disabled={isSaving}
          />
          <DstButton
            type="submit"
            variant="primary"
            value={translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.SAVE")}
            clickFunction={_handleSubmit}
            disabled={isSaving || !_isFormValid || !_hasChanges}
            loading={isSaving}
          />
        </React.Fragment>
      }
    >
      <Form id="edit-cursus-stage-form" onSubmit={_handleSubmit}>
        <DstTabsVertical
          activeKey={activeTab}
          bodyClass="medium-gray-background p-3"
          classes="pt-0 edit-cursus-stage-tabs"
          onSelect={(key) => key && setActiveTab(key)}
          titleIcon={<React.Fragment></React.Fragment>}
          variant="light"
          TabsSize={{ xs: 12, sm: 4, md: 3, lg: 3, xl: 3 }}
          tabsData={[
            {
              id: "general",
              title: _tabTitle("OVERALL_INFORMATION"),
              content: overallInformationTab,
            },
            {
              id: "format",
              title: _tabTitle("FORMAT"),
              content: formatTab,
            },
            {
              id: "contractualization",
              title: _tabTitle("CONTRACTUALIZATION"),
              content: contractualizationTab,
            },
          ]}
        />
      </Form>
    </Popup>
  );
};

export default EditCursusStagePopup;
